import { Injectable, inject } from '@angular/core';

import { Observable, finalize, map, of, shareReplay, tap } from 'rxjs';

import { PackageContent } from '../models/package-configurator/package.model';
import { PackageConfiguratorService } from '../services/package-configurator.service';

/**
 * Root cache for package contents (coverage bodies: pricing + options), keyed by coverage id. A body is
 * fetched once and reused on every later expand — including after leaving and reopening the configurator —
 * with one shared request per coverage while it is in flight. Saves, template inserts and cancellations
 * write through `set()` / `remove()`, so a cached body never lags behind what the backend holds.
 *
 * Callers get a copy each time, and the start date always comes from the caller's current coverage
 * reference, since a reschedule changes the reference without changing the body.
 */
@Injectable({ providedIn: 'root' })
export class PackageContentsStore {
  private readonly service = inject(PackageConfiguratorService);

  private readonly contents = new Map<string, PackageContent>();
  private readonly requests = new Map<string, Observable<PackageContent>>();

  /** The content's body from the cache; fetches it once if it is not cached yet. */
  load(packageId: string, coverageId: string, activeFrom: string | null): Observable<PackageContent> {
    const cached = this.contents.get(coverageId);
    if (cached) return of(copyWithDate(cached, activeFrom));

    let request$ = this.requests.get(coverageId);
    if (!request$) {
      request$ = this.service.getCoverage(packageId, coverageId, activeFrom).pipe(
        tap(content => this.contents.set(coverageId, structuredClone(content))),
        // Settled either way: a failure leaves nothing cached, so the next load() retries.
        finalize(() => this.requests.delete(coverageId)),
        shareReplay(1)
      );
      this.requests.set(coverageId, request$);
    }

    return request$.pipe(map(content => copyWithDate(content, activeFrom)));
  }

  /** Stores a content's latest saved body — after a save or a create from template. */
  set(content: PackageContent): void {
    this.contents.set(content.id, structuredClone(content));
  }

  /** Forgets a content — after it is cancelled, or when a save replaced it under a new id. */
  remove(coverageId: string): void {
    this.contents.delete(coverageId);
    this.requests.delete(coverageId);
  }
}

function copyWithDate(content: PackageContent, activeFrom: string | null): PackageContent {
  return { ...structuredClone(content), activeFrom };
}
